import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Chip, Grid } from "@mui/material";
import { useConditional } from "./useConditional.js";
import { useFormInspect, useFormPreviewMode } from "./contexts.js";
import ControlFactory from "./ControlFactory.jsx";

export default function FormDefinitionField({ item }) {
  const conditionallyHidden = useConditional(item.hidden);
  const { setValue } = useFormContext();
  const inspect = useFormInspect();
  const previewMode = useFormPreviewMode();
  // In preview mode the gating control is disabled and can't be toggled, so
  // conditionally-hidden fields would otherwise never be reachable — show
  // everything regardless of the live condition.
  const isHidden = previewMode ? false : conditionallyHidden;

  // Clear hidden field values so their required rules don't fail isValid
  useEffect(() => {
    if (isHidden && item.name) {
      setValue(item.name, null, { shouldValidate: true, shouldDirty: false });
    }
  }, [isHidden, item.name, setValue]);

  if (isHidden) return null;

  // item.cols is either a plain number/numeric string applied from `md` upward,
  // or a per-breakpoint object (e.g. { xxl: 4 }) applied from that breakpoint
  // upward — MUI's min-width media queries let unset breakpoints inherit the
  // nearest smaller one, so a sparse object still cascades correctly.
  const gridSize =
    item.cols && typeof item.cols === "object"
      ? { xs: 12, ...item.cols }
      : { xs: 12, md: Number(item.cols) || 12 };
  const isSelected = inspect && item.__id && inspect.selectedId === item.__id;
  const isConditional = !!(item.hidden && typeof item.hidden === "object");
  const dependents = previewMode?.dependentsByField?.get(item.name) ?? [];

  return (
    <Grid
      size={gridSize}
      ref={inspect ? (el) => inspect.registerNodeRef(item.__id, el) : undefined}
      onClick={
        inspect
          ? (e) => {
              e.stopPropagation();
              inspect.onSelectNode(item.__id);
            }
          : undefined
      }
      sx={
        inspect
          ? {
              cursor: "pointer",
              outline: isSelected ? "2px solid" : "2px solid transparent",
              outlineColor: isSelected ? "primary.main" : "transparent",
              outlineOffset: "2px",
              borderRadius: 1,
            }
          : undefined
      }
    >
      {previewMode && (isConditional || dependents.length > 0) && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
          {isConditional && (
            <Chip
              size="small"
              variant="filled"
              color="info"
              label="bedingt sichtbar"
              sx={{ fontSize: "10px" }}
            />
          )}
          {dependents.length > 0 && (
            <Chip
              size="small"
              variant="filled"
              color="warning"
              label={`Steuert: ${dependents.map((d) => d.label || d.name).join(", ")}`}
              sx={{ fontSize: "10px" }}
            />
          )}
        </Box>
      )}
      <ControlFactory item={item} />
    </Grid>
  );
}
